import { useMemo } from 'react';

import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import ChatbotIcon from '#/assets/svgs/ChatbotIcon.svg';
import { CHATBOT_ROLE, DATE_FORMAT } from '#/utils/constants';
import { LoadingDots } from '..';

function ChatMessages({
  messages,
  color,
  textColor,
  chatBotImage,
  reactions,
  handleReaction,
  isSending,
  isStreaming,
  isStreamingLoading,
  streamingMessage,
  isConversationHistoryFetching,
  streamingMessageRef,
}) {
  // Memoize the markdown components to prevent unnecessary re-renders
  const markdownComponents = useMemo(
    () => ({
      a: ({ ...props }) => <a {...props} target="_blank" />,
    }),
    []
  );

  // Memoize the current time to avoid recalculating on every render
  const currentTime = useMemo(() => dayjs().format(DATE_FORMAT.STANDARD_TIME), []);

  return (
    <>
      {messages.map(({ text, created, role, id, reaction }) => (
        <div
          key={id || `${role}-${created}`}
          className={`message ${role === CHATBOT_ROLE.USER ? 'sender' : 'receiver'}`}
        >
          {role !== CHATBOT_ROLE.USER && (
            <div className="image">{chatBotImage ? <img src={chatBotImage} alt="Chatbot" /> : <ChatbotIcon />}</div>
          )}
          <div className="text-wrapper">
            {role === CHATBOT_ROLE.USER ? (
              <p style={{ backgroundColor: color, color: textColor }}>{text}</p>
            ) : (
              <div className="markdown-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {text}
                </ReactMarkdown>
              </div>
            )}
            <div className="date-reactions">
              <span>{`${role === CHATBOT_ROLE.ASSISTANT ? 'Livechat' : ''} ${dayjs(created).format(DATE_FORMAT.STANDARD_TIME)}`}</span>
              {role === CHATBOT_ROLE.ASSISTANT && reactions?.data?.results?.length > 0 && (
                <div className="reactions">
                  {reactions.data.results.map(({ id: emojiId, emoji }) => (
                    <span
                      key={emojiId}
                      onClick={handleReaction(id, text, emojiId, reaction)}
                      className={`${emojiId === reaction ? 'active' : ''} ${isConversationHistoryFetching ? 'disabled' : ''}`}
                    >
                      <img src={emoji} alt="emoji" />
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
      {isStreaming && streamingMessage && (
        <div ref={streamingMessageRef} className="message receiver streaming">
          <div className="image">{chatBotImage ? <img src={chatBotImage} alt="Chatbot" /> : <ChatbotIcon />}</div>
          <div className="text-wrapper">
            <div className="markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {streamingMessage}
              </ReactMarkdown>
              <span className="streaming-cursor">|</span>
            </div>
            <div className="date-reactions">
              <span>Livechat {currentTime}</span>
            </div>
          </div>
        </div>
      )}
      {isStreamingLoading && <LoadingDots chatBotImage={chatBotImage} />}
      {isSending && !isStreaming && !isStreamingLoading && <LoadingDots chatBotImage={chatBotImage} />}
    </>
  );
}

export default ChatMessages;
